/**
 * Hotcoin WebSocket API
 * @see https://www.hotcoin.com/en_US/docs/#access-instructions-3
 */

import pako from 'pako';

import type {
  HotcoinWsDepthMessage,
  HotcoinWsKlineMessage,
  HotcoinWsPing,
  HotcoinWsPong,
  HotcoinWsSubscribeRequest,
  HotcoinWsSubscribeResponse,
  HotcoinWsTickerMessage,
  HotcoinWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

/**
 * Hotcoin WebSocket base URL.
 * @see https://www.hotcoin.com/en_US/docs/#access-instructions-3
 */
export const HOTCOIN_WS_BASE_URL = 'wss://wss.hotcoinfin.com/trade/multiple';

/**
 * Hotcoin WebSocket channel descriptors.
 * @see https://www.hotcoin.com/en_US/docs/#access-instructions-3
 */
export const HOTCOIN_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: 'market.<symbol>.trade.depth',
    supportsBatching: false,
    docUrl: 'https://www.hotcoin.com/en_US/docs/#market-depth-data',
  },
  ticker: {
    channel: 'ticker',
    topic: 'market.<symbol>.24h.tickers',
    supportsBatching: false,
    docUrl: 'https://www.hotcoin.com/en_US/docs/#24h-aggregate-market-data',
  },
  klines: {
    channel: 'klines',
    topic: 'market.<symbol>.kline.<period>',
    supportsBatching: false,
    docUrl: 'https://www.hotcoin.com/en_US/docs/#kline-data',
  },
  trades: {
    channel: 'trades',
    topic: 'market.<symbol>.trade.detail',
    supportsBatching: false,
    docUrl: 'https://www.hotcoin.com/en_US/docs/#real-time-transaction-details',
  },
  bbo: {
    channel: 'bbo',
    topic: 'market.<symbol>.trade.bbo',
    supportsBatching: false,
    docUrl: 'https://www.hotcoin.com/en_US/docs/#buy-and-sell-every-price',
  },
};

/**
 * Hotcoin WebSocket topology information.
 * CRITICAL: All messages are GZIP-compressed (like HTX) and require decompression before JSON parsing.
 * Server sends {"ping":"ping"}, client must respond {"pong":"pong"} every 5 seconds.
 */
export const HOTCOIN_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: HOTCOIN_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 5000, // Server sends ping every 5 seconds
  note: 'All messages are GZIP compressed (like HTX). Server sends {"ping":"ping"}, client responds {"pong":"pong"}. Orderbook sends full snapshots (not incremental).',
};

// ========================================
// GZIP Decompression Utilities
// ========================================

/**
 * Decode Hotcoin WebSocket message (GZIP-compressed).
 * Handles both ArrayBuffer and Blob payloads.
 * Similar to HTX - uses pako for decompression.
 *
 * @example
 * const payload = await decodeHotcoinWsMessage(event);
 * if (isHotcoinPing(payload)) { ws.send(JSON.stringify({ pong: 'pong' })); }
 */
export async function decodeHotcoinWsMessage(
  event: MessageEvent<ArrayBuffer | Blob | string>,
): Promise<unknown> {
  // Handle plain string (shouldn't happen but defensive)
  if (typeof event.data === 'string') {
    return JSON.parse(event.data);
  }

  try {
    // Convert Blob to ArrayBuffer if needed
    const buffer = event.data instanceof Blob ? await event.data.arrayBuffer() : event.data;

    // Decompress GZIP
    const text = pako.ungzip(new Uint8Array(buffer), { to: 'string' });

    return JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Hotcoin WebSocket decode error: ${message}`);
  }
}

// ========================================
// Type Guards
// ========================================

/**
 * Type guard to check if a message is a Hotcoin ping.
 * Server sends {"ping":"ping"} that requires {"pong":"pong"} response.
 */
export function isHotcoinPing(msg: unknown): msg is HotcoinWsPing {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'ping' in msg &&
    (msg as HotcoinWsPing).ping === 'ping'
  );
}

/**
 * Type guard to check if a message is a Hotcoin subscription response.
 */
export function isHotcoinSubscribeResponse(msg: unknown): msg is HotcoinWsSubscribeResponse {
  return (
    typeof msg === 'object' && msg !== null && ('status' in msg || ('code' in msg && 'msg' in msg))
  );
}

/**
 * Type guard to check if a message is a Hotcoin depth message.
 */
export function isHotcoinDepthMessage(msg: unknown): msg is HotcoinWsDepthMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'ch' in msg &&
    typeof (msg as HotcoinWsDepthMessage).ch === 'string' &&
    (msg as HotcoinWsDepthMessage).ch.includes('.trade.depth') &&
    'data' in msg
  );
}

/**
 * Type guard to check if a message is a Hotcoin trade message.
 */
export function isHotcoinTradeMessage(msg: unknown): msg is HotcoinWsTradeMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'ch' in msg &&
    typeof (msg as HotcoinWsTradeMessage).ch === 'string' &&
    (msg as HotcoinWsTradeMessage).ch.includes('.trade.detail') &&
    'data' in msg
  );
}

/**
 * Type guard to check if a message is a Hotcoin kline message.
 */
export function isHotcoinKlineMessage(msg: unknown): msg is HotcoinWsKlineMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'ch' in msg &&
    typeof (msg as HotcoinWsKlineMessage).ch === 'string' &&
    (msg as HotcoinWsKlineMessage).ch.includes('.kline.') &&
    'data' in msg
  );
}

/**
 * Type guard to check if a message is a Hotcoin ticker message.
 */
export function isHotcoinTickerMessage(msg: unknown): msg is HotcoinWsTickerMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'ch' in msg &&
    typeof (msg as HotcoinWsTickerMessage).ch === 'string' &&
    (msg as HotcoinWsTickerMessage).ch.includes('.24h.tickers') &&
    'data' in msg
  );
}

// ========================================
// Subscription Message Builders
// ========================================

/**
 * Creates a Hotcoin WebSocket subscription message.
 */
export function createHotcoinSubscribe(topic: string, id?: string): HotcoinWsSubscribeRequest {
  return {
    sub: topic,
    id: id ?? `sub_${Date.now()}`,
  };
}

/**
 * Creates a Hotcoin pong response message.
 */
export function createHotcoinPong(): HotcoinWsPong {
  return { pong: 'pong' };
}

// ========================================
// Channel Builders
// ========================================

/**
 * Builds the depth (orderbook) channel topic for a symbol.
 * @example buildHotcoinDepthChannel('btc_usdt') // 'market.btc_usdt.trade.depth'
 */
export function buildHotcoinDepthChannel(symbol: string): string {
  return `market.${symbol.toLowerCase()}.trade.depth`;
}

/**
 * Builds the ticker channel topic for a symbol.
 * @example buildHotcoinTickerChannel('btc_usdt') // 'market.btc_usdt.24h.tickers'
 */
export function buildHotcoinTickerChannel(symbol: string): string {
  return `market.${symbol.toLowerCase()}.24h.tickers`;
}

/**
 * Builds the trades channel topic for a symbol.
 * @example buildHotcoinTradesChannel('btc_usdt') // 'market.btc_usdt.trade.detail'
 */
export function buildHotcoinTradesChannel(symbol: string): string {
  return `market.${symbol.toLowerCase()}.trade.detail`;
}

/**
 * Builds the klines channel topic for a symbol and period.
 * @param symbol - Trading pair (e.g., 'btc_usdt')
 * @param period - Kline interval in seconds (e.g., '60', '300', '3600')
 * @example buildHotcoinKlinesChannel('btc_usdt', '60') // 'market.btc_usdt.kline.60'
 */
export function buildHotcoinKlinesChannel(symbol: string, period: string): string {
  return `market.${symbol.toLowerCase()}.kline.${period}`;
}

/**
 * Builds the BBO (Best Bid/Offer) channel topic for a symbol.
 * @example buildHotcoinBboChannel('btc_usdt') // 'market.btc_usdt.trade.bbo'
 */
export function buildHotcoinBboChannel(symbol: string): string {
  return `market.${symbol.toLowerCase()}.trade.bbo`;
}
